import { Node, Edge, MarkerType } from '@xyflow/react';
import { GRID_CONFIG } from '../gridConfig';
import type { Result1 } from '@mm-shared/types/Result1';
import { filterItemsForInterval } from '../../utils/statusUtils';
import { ResultCircleNodeData } from '../components/ResultCircleNode';
import { DraggableResultCircleNodeData } from '../components/DraggableResultCircleNode';
import { RelativesMapData } from '../components/Map3Relatives';
import { isStatusColumn, getLocalXForColumn } from './layoutMath';
import { getEdgeIdFromDependencyRecord, type DependencyRecord } from './dependencyUtils';

/**
 * Creates custom ReactFlow nodes.
 *
 * Result nodes are made from RelativesMapData as the single source of truth for
 * positioning and layout information.
 *
 * NOTE: This generates node positions x and y relative to the map
 * they're on. x and y both need to be offset by their positioning
 * on the master grid by whoever is calling this function.
 *
 * Base positioning:
 * - x: Relative to objective column width + column index
 * - y: Relative to header height + row index (or title bar for collapsed maps)
 *
 * Collapse behavior:
 * - expanded: result nodes in their objective's row, interactive
 * - titleOnly/fullyCollapsed: all nodes at title bar row (y=0), behind title bar, not interactive
 */
export function createResultNodesFromRelativesMapData(
  relativeMapData: RelativesMapData,
  enableFill: boolean = true,
  dragAndConnectModeResultUuid: string | null = null,
  onEnterDragAndConnectMode?: (resultUuid: string) => void,
  onExitDragAndConnectMode?: (resultUuid: string) => void,
  showStatusCodes: boolean = false,
  fullyCollapsedIndex: number = 0,
  totalFullyCollapsedMaps: number = 1,
): Node<ResultCircleNodeData | DraggableResultCircleNodeData>[] {
  const { objectives, milestoneMap, columnsInfo, collapseLevel, heightByRows } = relativeMapData;

  // Determine positioning and interaction based on collapse level
  const isCollapsed = collapseLevel === 'titleOnly' || collapseLevel === 'fullyCollapsed';
  const nodeZIndex = isCollapsed ? GRID_CONFIG.zIndex.collapsedNodes : GRID_CONFIG.zIndex.nodes;

  // Note: For fullyCollapsed maps, result nodes are positioned at the center of the wrapper

  return objectives.flatMap((objective, rowIndex) => {
    if (milestoneMap.parentObjectiveUuids?.includes(objective.uuid)) return [];

    return columnsInfo.flatMap((column, columnIndex) => {
      // Use filterItemsForInterval to find results that belong in this interval.
      // Note: We're checking all intervals on the master grid here, not just the ones on the map.
      const resultsForInterval = filterItemsForInterval(
        objective.results,
        column.startDate,
        column.endDate,
      );
      if (resultsForInterval.length > 0 && !isStatusColumn(column)) {
        // This should never happen. This means a result got matched to a date that isn't on
        // its corresponding map.
        console.error(
          `[createResultNodesFromRelativesMapData] Result ${resultsForInterval[0].uuid} got matched to a date that isn't on its corresponding map.`,
        );
      }

      // Get the x position using local column positioning
      const x = getLocalXForColumn(column);

      // Calculate y position based on collapse level
      let y: number;
      if (collapseLevel === 'fullyCollapsed') {
        // For fullyCollapsed maps, position ALL result nodes at the center of the wrapper
        // This creates a unified appearance regardless of which specific map they belong to
        // Since fullyCollapsed nodes have no parentId, they need global positioning
        y = relativeMapData.mapYOffset;
      } else if (collapseLevel === 'titleOnly') {
        // All result nodes positioned at title bar row (behind the title bar)
        y = 0;
      } else {
        // Normal positioning: objective's row
        y = GRID_CONFIG.headerHeight + GRID_CONFIG.cellHeight + rowIndex * GRID_CONFIG.cellHeight;
      }

      // Check if this cell contains the result that's in drag and connect mode
      const cellContainsDragAndConnectModeResult = dragAndConnectModeResultUuid
        ? resultsForInterval.some((r) => r.uuid === dragAndConnectModeResultUuid)
        : false;

      // Create a node for each result, using columnsInfo for positioning
      return resultsForInterval.map((result: Result1, resultIndex) => {
        // Find the status for this interval (needed for circle color)
        const status = objective.statuses.find(
          (s) => s.forIntervalEnding === column.endDate.toISOString(),
        );
        const statusCode = status?.statusCode || '';

        // Determine if this result should be in drag and connect mode.
        // ALL results in the cell become draggable when any one is clicked.
        // (From a user experience perspective, the one note clicked becomes bigger
        //  and is the only one visible and available for dragging and connecting.
        // But for simplicity, all the nodes in the cell change from ResultCircleNode to
        // DraggableResultCircleNode.)
        const isInDragAndConnectMode = cellContainsDragAndConnectModeResult;
        // This one will be on top. When there is more than one result, they overlap.
        // The one on top is the only one the user sees and interacts with.
        const isTheClickedResult = result.uuid === dragAndConnectModeResultUuid;

        // Always use circle nodes (ResultNode removed)
        let nodeData;
        let nodeType;

        if (isInDragAndConnectMode) {
          // Use draggable version for drag and connect mode
          nodeData = {
            result,
            objectiveUuid: objective.uuid,
            rowIndex,
            columnIndex,
            zoomLevel: relativeMapData.milestoneMap.interval,
            statusCode,
            enableFill: enableFill,
            showStatusCodes,
            onExitDragAndConnectMode,
          };
          nodeType = 'draggableResultCircleNode';
        } else {
          // Use visual layout version for display mode
          nodeData = {
            result,
            objectiveUuid: objective.uuid,
            rowIndex,
            columnIndex,
            zoomLevel: relativeMapData.milestoneMap.interval,
            resultIndex,
            totalResults: resultsForInterval.length,
            siblingResults: resultsForInterval,
            statusCode,
            enableFill: enableFill,
            showStatusCodes,
            onEnterDragAndConnectMode,
          };
          nodeType = 'resultCircleNode';
        }

        const node = {
          id: result.uuid,
          type: nodeType,
          position: {
            x,
            y,
          },
          data: nodeData,
          style: {
            // Put the originally clicked result on top with higher z-index
            // For fullyCollapsed, use collapsedNodes z-index (12) - behind thick lines (35) but above grid (10)
            zIndex:
              collapseLevel === 'fullyCollapsed'
                ? isTheClickedResult
                  ? GRID_CONFIG.zIndex.collapsedNodes + 1
                  : GRID_CONFIG.zIndex.collapsedNodes
                : isTheClickedResult
                  ? nodeZIndex + 1
                  : nodeZIndex,
          },
          // Make nodes interactive and draggable when not collapsed
          // For circle nodes, only draggable when in drag and connect mode
          draggable: !isCollapsed && isInDragAndConnectMode,
          selectable: !isCollapsed && isInDragAndConnectMode,
          // Result nodes are children of their respective map nodes (fullyCollapsed nodes have no parent since thick-line nodes are external)
          ...(collapseLevel === 'fullyCollapsed'
            ? {}
            : {
                parentId: `map-${relativeMapData.milestoneMap.uuid}`,
                extent: 'parent' as const,
              }),
        };
        return node;
      });
    });
  });
}

type DependencyEdge = Edge<any> & {
  type: 'default';
  markerEnd: typeof EDGE_CONFIG.markerEnd;
  style: typeof EDGE_CONFIG.style;
  animated: boolean;
  className: string;
  zIndex: number;
};

/**
 * Shared edge configuration for consistent styling of dependencies
 */
const EDGE_CONFIG = {
  type: 'default',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 8,
    height: 12,
    color: '#374151', // Gray-700
  },
  style: {
    stroke: '#374151', // Gray-700
    strokeWidth: 2,
    transition: 'none', // Disable CSS transitions to prevent "flies in from upper left" animation
  },
  animated: false,
  className: 'dependency-edge',
  zIndex: GRID_CONFIG.zIndex.edges,
} as const;

/**
 * Creates edges from node dependencies and applies visibility filtering
 */
export function createEdgesFromDependencies(
  nodes: Node[],
  selectedEdges: Edge[],
  dependencyRecords: DependencyRecord[],
): Edge[] {
  const seenEdges = new Set<string>();

  // Step 1: Create all edges from local node data (for immediate UI responsiveness)
  const allEdges = nodes.flatMap((node) => {
    const nodeData = node.data as ResultCircleNodeData | DraggableResultCircleNodeData;
    const result = nodeData.result;
    if (!result?.dependencies?.length) return [];

    return result.dependencies
      .map((dep): DependencyEdge | null => {
        // Create a minimal DependencyRecord for edge ID generation
        const depRecord: DependencyRecord = {
          sourceObjective: { uuid: nodeData.objectiveUuid, text: 'stub' },
          sourceResult: { uuid: result.uuid, text: result.text, isKeyResult: result.isKeyResult },
          sourceMap: { uuid: 'stub', text: 'stub' },
          targetObjective: { uuid: 'stub', text: 'stub' },
          targetResult: { uuid: dep.targetResultUuid, text: 'stub', isKeyResult: false },
          targetMap: { uuid: 'stub', text: 'stub' },
          sourceMapInterval: 'months',
          sourceResultDeadline: '',
          targetMapInterval: 'months',
          targetResultDeadline: '',
          hasDeadlineConflict: false,
        };

        // TODO: Revisit. Now that we can have the same objective/results on multiple maps,
        // we should think about what happens with IDs like this (that can point to two different
        // instances of the same result) and what we should do with this.
        const edgeId = getEdgeIdFromDependencyRecord(depRecord);

        // Skip if we've already created this edge
        if (seenEdges.has(edgeId)) return null;
        seenEdges.add(edgeId);

        // Check if this edge is selected
        const isSelected = selectedEdges.some((e) => e.id === edgeId);

        return {
          id: edgeId,
          source: result.uuid,
          target: dep.targetResultUuid,
          type: EDGE_CONFIG.type,
          markerEnd: EDGE_CONFIG.markerEnd,
          style: EDGE_CONFIG.style,
          animated: EDGE_CONFIG.animated,
          className: `${EDGE_CONFIG.className}${isSelected ? ' selected' : ''}`,
          zIndex: EDGE_CONFIG.zIndex,
        };
      })
      .filter((edge): edge is DependencyEdge => edge !== null);
  });

  // Step 2: Apply visibility filtering based on dependencyRecords (user's map options dropdown choices)
  const visibleDependencyIds = new Set(
    dependencyRecords.map((dep) => getEdgeIdFromDependencyRecord(dep)),
  );

  return allEdges.filter((edge) => visibleDependencyIds.has(edge.id));
}
